import React from "react";
import Cricket from "../assets/Cricket.jpg";
import Football from "../assets/Football.jpg";
import Basketball from "../assets/Basketball.jpg";
import Athletics from "../assets/Athletics.avif";
import Yoga from "../assets/Yoga.webp";
import Literary from "../assets/Literary Club.png";
import Drama from "../assets/Drama Club.jpg";
import Music from "../assets/Music Band.avif";
import EnvironmentClub from "../assets/Environment Club.avif";
import HealthCampaigns from "../assets/Health Awareness Campaigns.png";
import SportsTournaments from "../assets/Sports Tournaments.avif";
import CulturalFestivals from "../assets/Cultural Festivals.webp";
import ScienceSociety from "../assets/Science Society.jpg";
import ArtCraftClub from "../assets/Art and Craft Club.webp";
import ScienceMathFairs from "../assets/sci-math.webp";

const CLUBS = [
  {
    name: "Literary Club",
    img: Literary,
    msg: "Creative writing, debates, and discussions to improve communication.",
  },
  {
    name: "Science Society",
    img: ScienceSociety,
    msg: "Experiments, projects, and competitions to build scientific curiosity.",
  },
  {
    name: "Art and Craft Club",
    img: ArtCraftClub,
    msg: "Hands-on creative expression through painting, crafting, and design.",
  },
];

const SPORTS = [
  {
    name: "Cricket",
    img: Cricket,
    msg: "Team discipline, strategic thinking, and sportsmanship.",
  },
  {
    name: "Football",
    img: Football,
    msg: "Speed, endurance, and coordination through team play.",
  },
  {
    name: "Basketball",
    img: Basketball,
    msg: "Fast-paced game that strengthens focus and teamwork.",
  },
  {
    name: "Athletics",
    img: Athletics,
    msg: "Track and field events to enhance strength and stamina.",
  },
  {
    name: "Yoga",
    img: Yoga,
    msg: "Mind-body balance, flexibility, and concentration.",
  },
];

const PERFORMING_ARTS = [
  {
    name: "Drama Club",
    img: Drama,
    msg: "Confidence building through stage performance and storytelling.",
  },
  {
    name: "Music Band",
    img: Music,
    msg: "Rhythm, coordination, and collaboration through music.",
  },
];

const COMMUNITY_SERVICE = [
  {
    name: "Environment Club",
    img: EnvironmentClub,
    msg: "Activities and campaigns to build environmental responsibility.",
  },
  {
    name: "Health Awareness Campaigns",
    img: HealthCampaigns,
    msg: "Programs that encourage healthy habits and community wellness.",
  },
];

const COMPETITIONS = [
  {
    name: "Science and Math Fairs",
    img: ScienceMathFairs,
    msg: "Showcase innovation, problem-solving, and practical thinking.",
  },
  {
    name: "Cultural Festivals",
    img: CulturalFestivals,
    msg: "Celebrate creativity, diversity, and performing arts.",
  },
  {
    name: "Sports Tournaments",
    img: SportsTournaments,
    msg: "Inter-school competitions that foster competitive spirit.",
  },
];

function SectionTitle({ title }) {
  return (
    <h2 className="text-2xl font-semibold text-slate-900 sm:text-3xl">{title}</h2>
  );
}

function ActivityCard({ name, img, msg }) {
  return (
    <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-md transition hover:-translate-y-1 hover:shadow-lg">
      <img src={img} alt={name} className="h-44 w-full object-cover" />
      <div className="p-4">
        <h3 className="text-lg font-semibold text-slate-900">{name}</h3>
        {msg ? <p className="mt-2 text-sm leading-6 text-slate-700">{msg}</p> : null}
      </div>
    </article>
  );
}

function ActivityGrid({ items }) {
  return (
    <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {items.map((item) => (
        <ActivityCard key={item.name} {...item} />
      ))}
    </div>
  );
}

function Academics() {
  return (
    <main className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6 lg:px-10">
      <div className="mx-auto max-w-7xl space-y-6">
        <section className="rounded-3xl bg-gradient-to-r from-blue-900 via-blue-800 to-blue-700 p-8 text-white shadow-xl">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-blue-100">
            Academics
          </p>
          <h1 className="mt-3 text-3xl font-semibold text-white sm:text-4xl">
            Structured Learning, Holistic Growth
          </h1>
          <p className="mt-3 max-w-4xl text-sm leading-6 text-blue-100 sm:text-base">
            Our academic model follows the CBSE pattern and combines classroom
            excellence with clubs, sports, arts, and community engagement.
          </p>

          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            <div className="rounded-2xl border border-white/20 bg-white/10 p-4">
              <p className="text-xs uppercase tracking-[0.2em] text-blue-100">
                Curriculum
              </p>
              <p className="mt-2 text-lg font-semibold text-white">
                CBSE Aligned
              </p>
            </div>
            <div className="rounded-2xl border border-white/20 bg-white/10 p-4">
              <p className="text-xs uppercase tracking-[0.2em] text-blue-100">
                Learning Style
              </p>
              <p className="mt-2 text-lg font-semibold text-white">
                Concept + Practice
              </p>
            </div>
            <div className="rounded-2xl border border-white/20 bg-white/10 p-4">
              <p className="text-xs uppercase tracking-[0.2em] text-blue-100">
                Student Growth
              </p>
              <p className="mt-2 text-lg font-semibold text-white">
                Academic + Co-Curricular
              </p>
            </div>
          </div>
        </section>

        <section className="grid gap-6 lg:grid-cols-2">
          <article className="rounded-3xl border border-slate-200 bg-white p-6 shadow-md">
            <SectionTitle title="Curriculum Overview" />
            <p className="mt-3 text-sm leading-7 text-slate-700 sm:text-base">
              The curriculum follows the CBSE pattern and is designed to build
              strong conceptual understanding, analytical thinking, and
              practical application across subjects.
            </p>
          </article>

          <article className="rounded-3xl border border-slate-200 bg-white p-6 shadow-md">
            <SectionTitle title="Departments" />
            <p className="mt-3 text-sm leading-7 text-slate-700 sm:text-base">
              Our academic departments collaborate to provide balanced learning
              pathways, regular assessments, and personalized support based on
              student needs.
            </p>
          </article>
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-md">
          <SectionTitle title="Extracurricular Activities" />
          <p className="mt-3 text-sm leading-7 text-slate-700 sm:text-base">
            We believe education extends beyond textbooks. Through clubs, sports,
            arts, and social projects, students develop confidence, teamwork,
            leadership, and real-world life skills.
          </p>

          <div className="mt-8 space-y-8">
            <section>
              <h3 className="text-xl font-semibold text-slate-900">
                Clubs and Societies
              </h3>
              <ActivityGrid items={CLUBS} />
            </section>

            <section>
              <h3 className="text-xl font-semibold text-slate-900">Sports</h3>
              <ActivityGrid items={SPORTS} />
            </section>

            <section>
              <h3 className="text-xl font-semibold text-slate-900">
                Performing Arts
              </h3>
              <ActivityGrid items={PERFORMING_ARTS} />
            </section>

            <section>
              <h3 className="text-xl font-semibold text-slate-900">
                Community Service
              </h3>
              <ActivityGrid items={COMMUNITY_SERVICE} />
            </section>

            <section>
              <h3 className="text-xl font-semibold text-slate-900">
                Competitions and Events
              </h3>
              <ActivityGrid items={COMPETITIONS} />
            </section>
          </div>
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-md">
          <SectionTitle title="Conclusion" />
          <p className="mt-3 text-sm leading-7 text-slate-700 sm:text-base">
            At Rising Stars Public School, academics and activities work
            together to shape well-rounded learners. Students are encouraged to
            discover their strengths, build new skills, and grow with
            confidence.
          </p>
        </section>
      </div>
    </main>
  );
}

export default Academics;
